<template>
  <div class="content">
    <div class="main">

      <div class="container text-center">
        <h2>{{ title }}</h2>
      </div>

      <div class="container center col-8">
          <canvas id="sineCanvas"></canvas>
      </div>

      <div class="container text-left">
        <p>This is an animation of the frequency wave generated from a string.
          When plucking a string we tend to think of it vibrating back and forth in one dimension along a line.
          In actuality a string will tend toward rotating in a circle around the center of its axis.</p>

        <p>In this diagram the perspective is looking down the center of a string.
          When at rest the string would extend through the center point.
          When struck the string will settle into a circle about the axis.
          Plucking harder or softer, the amount of energy added, causes the circle to be larger or smaller,
          but the mean frequency of rotation will remain the same.
          The frequency of rotation is determined by a string's physical composition of length, diameter, material and tension.</p>

        <p>Vibration is the disruption of air caused by the string movement.
          When cirling toward the listener it is pushing air and increasing pressure on the ear.
          When cirling away it is creating a vacuum and decreasing pressure on the ear.
          The sound waves caused by string, horn or speaker vibration are not on/off pulses.
          The waves are a smooth, fluctuating gradient between minimum and maximum pressures, a sine wave.</p>

        <p>The human brain is very atuned to recognizing these variations in pressure and categorizing the mean frequency.
          It is also very good at separating out and tracking multiple frequencies at the same time.
          The brain recognizes and remembers a note by its mean frequency a.k.a tonal frequency or <strong>pitch</strong>.</p>

        <p><strong>Timbre</strong>, why instruments have different tones for the same note, are caused by variations in sound wave generation.
          Think of the path around the circle and generated wave as being imperfect and fuzzy,
          but centering around the mean frequency which our brain will categorize as the tonal frequency.
          These variations are timbre.
          The force applied when plucking a string is a straight line force with varying magnitude.
          It takes a certain amount of time for the string to settle into a circle.
          Conversely, there is a decay rate as the energy dissapates and the string settles back down to rest.
          Differences in string material and thickness effect these variations.
          Then add to that other factors such as the effects from an instrument body's size, shape and composition
          or the effects from electrical pickups, amplifiers and speakers.
          A computer generated tone is close to perfect and sounds dry and sometimes annoying,
          because it lacks the timbre variations our brain is trying to analyze.</p>
      </div>

      <div class="section">
        <a href="https://pages.mtu.edu/~suits/Physicsofmusic.html" target="_blank">B. H. Suits' Physics of Music course notes.</a>
      </div>

    </div>
  </div>
</template>
<script>
export default {
  name: "Sound",
  components: {
  },

  data() {
    return {
        title: "Sound Wave Frequency Generation",

        canvas: {},
        context: {},
        height: 0,
        width: 0,
        xAxis: 0,
        yAxis: 0,
        unit: 50,
        maxseconds: 10,
        seconds: 0,
        tvalue: 0,
    }
  },

  mounted: function () {
      this.init();
      this.animate();
  },
  methods: {
    init() {
        this.canvas = document.getElementById("sineCanvas");

        this.width = this.canvas.width;
        this.height = this.canvas.height;

        this.context = this.canvas.getContext("2d");
        this.context.font = '18px sans-serif';
        this.context.strokeStyle = '#000';
        this.context.lineJoin = 'round';

        this.xAxis = Math.floor(this.height / 2);
        this.yAxis = Math.floor(this.width / 4);

        this.context.save();
    },

    animate () {
        this.draw();

        // Update the time and draw again
        this.seconds = this.seconds + .050;
        this.tvalue = -this.seconds * Math.PI;
        if (this.seconds <= this.maxseconds)
            setTimeout(this.animate, 50);
    },

    /**
     * This function draws one frame of the animation, waits 20ms, and then calls
     * itself again.
     */
    draw () {
        // Clear the canvas
        this.context.clearRect(0, 0, this.width, this.height);

        // Draw the axes in their own path
        this.context.beginPath();
        this.drawAxes();
        this.context.stroke();

        // Set styles for animated graphics
        this.context.save();
        this.context.strokeStyle = '#00f';
        this.context.fillStyle = '#fff';
        this.context.lineWidth = 2;

        // Draw the sine curve at time draw.t, as well as the circle.
        this.context.beginPath();
        this.drawSine(this.tvalue);
        this.drawCircle();
        this.context.stroke();

        // Draw the arrow at time t in its own path.
        this.drawArrow(this.tvalue);

        // Restore original styles
        this.context.restore();

        // Draw the xAxis PI tick and the time
        this.context.fillText("Ï�", this.xAxis + 59 + this.unit * 3, this.xAxis + 18);
        this.context.fillText("t = " + Math.floor(Math.abs(this.seconds)), 10, 20);
    },

    drawAxes() {
        // Draw X and Y axes
        this.context.moveTo(0, this.xAxis);
        this.context.lineTo(this.width, this.xAxis);
        this.context.moveTo(this.yAxis, 0);
        this.context.lineTo(this.yAxis, this.height);

        // Draw X axis tick at PI
        this.context.moveTo(this.yAxis + Math.PI * this.unit, this.xAxis + 5);
        this.context.lineTo(this.yAxis + Math.PI * this.unit, this.xAxis - 5);
    },

    /**
     * The sine curve is drawn in 10px segments starting at the origin.
     */
    drawSine(t) {
        // Set the initial x and y, starting at 0,0 and translating to the origin on
        // the canvas.
        var x = t;
        var y = Math.sin(x);
        this.context.moveTo(this.yAxis, this.xAxis + this.unit * y);

        // Loop to draw segments
        for (var i = this.yAxis; i <= this.width; i += 10) {
            x = t + (i - this.yAxis) / this.unit;
            y = Math.sin(x);
            this.context.lineTo(i, this.xAxis + y * this.unit);
        }
    },

    drawCircle() {
        this.context.moveTo(this.yAxis + this.unit, this.xAxis);
        this.context.arc(this.yAxis, this.xAxis, this.unit, 0, 2 * Math.PI, false);
    },

    drawArrow(t) {
        // Cache position of arrow on the circle
        var x = this.yAxis + this.unit * Math.cos(t);
        var y = this.xAxis + this.unit * Math.sin(t);

        // Draw the arrow line
        this.context.beginPath();
        this.context.moveTo(this.yAxis, this.xAxis);
        this.context.lineTo(x, y);
        this.context.stroke();

        // Draw the arrow bead
        this.context.beginPath();
        this.context.arc(x, y, 5, 0, 2 * Math.PI, false);
        this.context.fill();
        this.context.stroke();

        // Draw dashed line to yAxis
        this.context.beginPath();
        var direction = (Math.cos(t) < 0) ? 1 : -1;
        //var start = (direction == -1) ? -5 : 0;
        for (var i = x;  direction * i < direction * this.yAxis - 5; i = i + direction * 10) {
            this.context.moveTo(i + direction * 5, y);
            this.context.lineTo(i + direction * 10, y);
        }
        this.context.stroke();

        // Draw yAxis bead
        this.context.beginPath();
        this.context.arc(this.yAxis, y, 5, 0, 2 * Math.PI, false);
        this.context.fill();
        this.context.stroke();
    }

  }
};

</script>
<style>
#sineCanvas {
    width: 800px;
    height: 300px;
}
</style>
